import React from "react"
import { Link } from "react-router-dom"
import { useModel, FormikControl } from "@stem-sims/nexus"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import Container from "react-bootstrap/Container"
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal"
import { Formik, Form } from "formik"
import { ClipLoader } from "react-spinners"
import { toast } from "react-toastify"
import * as Yup from "yup"
import customRequest from "../../helpers/customRequest"
import * as statusModel from '../../models/status'
import ExpirationMessage, { ExpiringMessage } from "../../components/ExpirationMessage"
import StudentRoster from "../../components/StudentRoster"
import DashboardRoster from "./components/DashboardRoster"

export default function Dashboard({ org }: { org: string }) {
    
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { response: status, loading: statusLoading } = useModel({
        model: statusModel.get
    })
    const readingStatus = status?.reading
    const mathStatus = status?.math
    const validLicenses = []
    if (readingStatus?.available >= 0) {
        validLicenses.push("Reading")
    }
    if (mathStatus?.available >= 0) {
        validLicenses.push("Math")
    }
    return <>
    <ExpirationMessage status={mathStatus} program="math" />
    <ExpirationMessage status={readingStatus} program="reading" />
    {status && <ExpiringMessage expiringSoon={status.expiringSoon}/>}
    <Container id="dashboard" fluid="lg" className='align-self-center pt-3 px-xl-3'>
        <Row className="mb-4 align-items-center mt-4">
            <Col md={4} xl={6}>
            <h1 className=" mb-0">
                <span className="d-none d-xl-inline">Great Leaps </span> 
                <span>Dashboard</span>
            </h1>
            </Col>
            <Col md={8} xl={6} className="text-end gx-0">
                <Link to="/help/frequently-asked-questions" className="btn btn-outline-success px-5">FAQ</Link>
                <a href={"/file/instructions"} target="_blank" rel="noreferrer" className="btn btn-outline-success mx-3">
                    Reading Instructions
                </a>
                <a href={"/file/math_instructions"} target="_blank" rel="noreferrer" className="btn btn-outline-success">
                    Math Instructions
                </a>
            </Col>
        </Row>
        <Row>
            <h2 className="section-header h3">Welcome {org}!</h2>
        </Row>
        <Row id="student-roster" className="align-items-center text-center pt-3 px-xl-5">
            <Col md={5}>
                <p id="student-search-text" className="mb-0">To begin an exercise, select student from the list below.</p>
            </Col>
            <StudentRoster Roster={DashboardRoster} validLicenses={validLicenses} />
        </Row>
        <p className="text-center w-100">
            To add or edit students, please proceed to your <Link to="/students">students page</Link>.
        </p>

        <h2 className="section-header text-start">Helpful Videos</h2>

        <div id="home-carousel" className="carousel slide indicators-bottom center-block" data-ride="carousel" data-interval="false">
            <div className="carousel-inner" role="listbox">
                <div className="carousel-item active">
                    <Row>
                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/vD98CEEIpMY?rel=0" title="video1" allowFullScreen></iframe>
                            </div>
                        </Col>

                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/3ni69UvQlA8?rel=0" title="video2" allowFullScreen></iframe>
                            </div>
                        </Col>
                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/ZolXJrJr6fU?rel=0" title="video3" allowFullScreen></iframe>
                            </div>
                        </Col>
                    </Row>
                </div>

                <div className="carousel-item">
                    <Row>
                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/h51TfIYyZ1A?rel=0" title="video4" allowFullScreen></iframe>
                            </div>
                        </Col>

                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/zU0xDgUO5aI?rel=0" title="video5" allowFullScreen></iframe>
                            </div>
                        </Col>

                        <Col sm={4}>
                            <div className="embed-responsive embed-responsive-16by9">
                                <iframe src="https://www.youtube-nocookie.com/embed/PVqHt6r1Wjw?rel=0" title="video6" allowFullScreen></iframe>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="carousel-controls carousel-controls-logged-in">
                <Row>
                    <Col sm={4}>
                        <a className="carousel-left carousel-control" href="#home-carousel" data-slide="prev">
                            <span className="fas fa-chevron-left video-arrow"></span>
                        </a>
                    </Col>
                    <Col sm={4}>
                        <ol className="carousel-indicators carousel-indicators-logged-in indicators-bottom">
                            <li data-target="#home-carousel" data-pause="true" data-slide-to="0" className="active"></li>

                            <li data-target="#home-carousel" data-slide-to="1"></li>
                        </ol>
                    </Col>
                    <Col sm={4}>
                        <a className="carousel-right carousel-control" href="#home-carousel" data-slide="next">
                            <span className="fas fa-chevron-right video-arrow"></span>
                        </a>
                    </Col>
                </Row>
            </div>
        </div>
        <ReferralForm />
        <UpdateModal />
        </Container>
    </>
}

function ReferralForm() {
    return (<>
        <h2 className="share-title-container text-center">
            <span className="share-title d-none d-sm-inline">Share Great Leaps with a student in need!</span>
            <span className="d-block d-sm-none">Share Great Leaps with a student in need!</span>
        </h2>
        <p className="text-center">Share Great Leaps Digital with your fellow parents, teachers, and tutors by entering their emails below and we'll send them an invitation.</p>
        <Formik
            initialValues={{
                referralEmails: ""
            }}
            validationSchema={
                Yup.object().shape({
                    referralEmails: Yup.string().required()
                })
            }
            onSubmit={async (values, actions) => {
                const {data: response} = await customRequest.post("/api/account/referral", {
                    emails: values.referralEmails
                })
                if (response.valid === false) {
                    toast.error(response.message)
                }
                else {
                    actions.resetForm()
                    toast.success("Your referral has been sent! Thank you.")
                }
            }}
        >
            {({ isSubmitting }) => (
                <Form className="form-horizontal form-validate">
                    <Row className="mb-3">
                        <Col className="offset-3 col-6">
                            <FormikControl
                                type="text"
                                className="form-control"
                                id="referralEmails"
                                name="referralEmails"
                                placeholder="Enter their emails separated with a comma"
                            />
                        </Col>
                        <Col>
                            <Button
                                type="submit"
                                variant="secondary"
                                className="btn-md text-center mt-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 
                                    <ClipLoader size={20} color={"#123abc"}/>
                                    : 
                                    "Send"
                                }
                            </Button>
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
        
    </>)
}

function UpdateModal() {
    const [showUpdate, setShowUpdate] = React.useState(false)
    React.useEffect(() => {
        customRequest.get("/api/update")
        .then(({data: response}) => {
            setShowUpdate(response.valid && !response.seen)
        })
    })

    return (
        <Modal show={showUpdate} size={"lg"}>
            <Modal.Header>
                <h2 className="modal-title text-center w-100" id="stop-modal-label">Exciting Updates to Great Leaps Digital</h2>
            </Modal.Header>

            <Modal.Body className="ps-5">
                <div className="text-start">
                    <p>Based on student performance data and feedback from instructors, we have redesigned our phonics section. We have changed the order of exercises. Thirty-five have been added in total, many targeting multisyllabic words. <b>Your students will be automatically directed to a new exercise number. Your student may be on a different phonics skill than before. Students will benefit from the added exercises and refinements.</b></p>
                </div>
                <p>As always if you have any questions feel free to contact our customer service department at <a href="mailto:glsupport@greatleaps.com">glinfo@greatleaps.com</a> or give us a call at <span className = "text-center">(877)&nbsp;475-3277</span></p>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary"  className="center-block" data-dismiss="modal" onClick={() => {
                    void customRequest.post("/api/update")
                    setShowUpdate(false)
                }}>Okay, got it.</Button>
            </Modal.Footer>
        </Modal>
    )
}
